<template>
	<section class="migration migration--verify">
		<template v-if="!error && !login && !resetPassword && !isLoggedIn">
			<h1 v-html="$t('migration.verify.title')"/>
			<div class="migration__text" v-html="$t('migration.verify.text')" />

			<GeneralMessage
				v-if="response.message"
				:message="response.message"
				:type="response.type"
				@close="response.message = ''"
			/>
			<ValidationObserver
				ref="form"
				v-slot="{ invalid, errors }"
				tag="form"
				@submit.prevent="onSubmit"
				novalidate
			>

				<ValidationProvider
					name="code"
					rules="required"
					v-slot="{ errors, invalid, validated }"
					tag="div"
					:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
				>
					<FormInput
						type="text"
						:placeholder="$t('migration.verify.codeLabel')"
						:label="$t('migration.verify.codeLabel')"
						v-model="form.token"
						:error="errors.length > 0"
						:error-message="errors[0]"
						:disabled="disabled"
						required
					/>
				</ValidationProvider>

				<div class="migration__text">
					<p>
						<Button
							level="tertiary"
							type="button"
							:text="$t('migration.verify.resendButton')"
							@click.prevent="onResend"
							:disabled="disabled"
							arrow
						/>
					</p>
				</div>

				<Button
					type="submit"
					level="primary"
					:text="$t('migration.verify.sendButton')"
					:disabled="disabled"
				/>
			</ValidationObserver>
		</template>
		<template v-else-if="!error && isLoggedIn">
			<h1 v-html="$t('migration.verify.loggedInTitle')"/>
			<div class="migration__text" v-html="$t('migration.verify.loggedInText')" />
			<Button
				level="primary"
				type="button"
				@click.prevent="goToAccount"
				:text="$t('migration.verify.loggedInButton')"
			/>
		</template>
		<template v-else-if="!error && login">
			<h1 v-html="$t('migration.verify.loginTitle')"/>
			<div class="migration__text" v-html="$t('migration.verify.loginText')" />
			<FormLogin
				@loading="setLoading"
			/>
		</template>
		<template v-else-if="!error && resetPassword">
			<h1 v-html="$t('migration.verify.resetTitle')"/>
			<div class="migration__text" v-html="$t('migration.verify.resetText')" />
			<FormPasswordReset
				:token="resetPasswordToken"
				@loading="setLoading"
				@resetted="onReset"
			/>
		</template>
		<template v-else>
			<h1 v-html="$t('migration.errorTitle')"/>
			<GeneralMessage
				:message="error"
				type="error"
				stick
			/>
			<Button
				level="primary"
				type="button"
				@click.prevent="onCancel"
				:text="$t('migration.cancelButton')"
			/>
		</template>
	</section>
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import { ValidationProvider, ValidationObserver } from "vee-validate"
import veevalidate from "@/helpers/veevalidate"
import GenericMixin from '@/mixins/GenericVueMixin'
import FormInput from '@/components/FormInput'
import FormLogin from '@/components/FormLogin'
import FormPasswordReset from '@/components/FormPasswordReset'
import Button from '@/components/Button'
import GeneralMessage from '@/components/GeneralMessage'
import leadingZero from '@/utils/leadingZero'
import sleep from '@/utils/sleep'
import {getUrlParams} from '@/utils/urlParams';
import htmlComponent from "htmlcomponent";


export default {
	mixins: [
		GenericMixin,
	],
	components: {
		ValidationObserver,
		ValidationProvider,
		FormInput,
		FormLogin,
		FormPasswordReset,
		Button,
		GeneralMessage,
	},
	data() {
		return {
			initialized: false,
			error: null,
			form: {
				token: ''
			},
			response: {
				type: 'info',
				message: '',
			},
			login: false,
			resetPassword: false,
			resetPasswordToken: '',
		}
	},
	computed: {
		...mapGetters("account", [
			"isLoggedIn"
		]),
		...mapGetters("migration", [
			"migrationEmail",
			"migrationBrands",
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		disabled() {
			return this.loading;
		}
	},
	watch: {
		isLoggedIn(value) {
			if (value) {
				this.loading = false;
			}
		}
	},
	methods: {
		async onSubmit() {
			const isValid = await this.$refs.form.validate()
			if (isValid) {
				this.loading = true;
				document.activeElement.blur();
				try {
					const response = await this.$store.dispatch('migration/verifyMigration', {
						email: this.migrationEmail,
						token: this.form.token,
					});

					this.login = !response.resetPassword;
					this.resetPassword = !!response.resetPassword;
					this.resetPasswordToken = response.resetPasswordToken;
				} catch(err) {
					let message = this.$t('forms.errors.generic')
					const data = err.response.data;

					if (data) {
						if (data.errors && data.errors.checkTerms) {
							message = data.errors.checkTerms[0]
						} else {
							message = data.message
						}
					}

					this.response.message = message;
					this.response.type = 'error';
				}
				this.loading = false;
			}
		},
		onReset() {
			this.resetPassword = false;
			this.login = true;
		},
		onCancel() {
			this.$store.dispatch('redirect', {name: 'home'});
		},
		async onResend() {
			this.loading = true;
			try {
				const response = await this.$store.dispatch('migration/startMigration', {
					email: this.migrationEmail,
					checkTerms: true,
				});

				this.response.message = this.$t('migration.verify.resendSuccess');
				this.response.type = 'success';
			} catch(err) {
				this.response.message = this.$t('forms.errors.generic');
				this.response.type = 'error';
			}
			this.loading = false;
		},
		goToAccount() {
			this.$store.dispatch('redirect', {name: 'account'});
		},
		setLoading(value) {
			this.loading = value;
		}
	},
	created() {
		this.loading = true;
	},
	async mounted() {
		this.loading = true;

		const params = getUrlParams();
		if (!params.email) {
			this.error = this.$t('forms.errors.generic');
		}
		else {
			this.$store.dispatch('migration/setEmail', { email: params.email });

			if (params.resend) {
				await this.onResend();
			}
		}

		this.initialized = true;
		this.loading = false;
	}
};
</script>

<style>
</style>
